import React, { useState, useContext, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";

import MetadataContext from "../../Context/metadataContext";
import { axiosInstance } from "../../utils/axios";

import FullPageSpinner from "../../Component/common/FullPageSpinner";
import ListFilter from "../../Component/FilterListUi/ListFilter";
import { DataListComponent } from "../../Component/DataList/DataListComponent";
import { ResultSortBy } from "../../Component/ResultSortBy/ResultSortBy";

import Header from "../../Component/UI/Header/Header";
import Hero from "../../Component/UI/Hero/Hero";
import Search from "../../Component/Search/Search";
import "../SearchList/SearchComponentLatest.css";

const HnHiring = () => {
  const { metadata, setMetadata, isLoading: metadataIsLoading } = useContext(MetadataContext);

  const autocompleteRef = useRef(null);

  const [autoCompleteList, setAutoCompleteList] = useState([]);
  const [counts, setCounts] = useState();
  const [queryphrase, setQueryphrase] = useState("");
  const [showOptions, setShowOptions] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [filterOptions, setFilterOptions] = useState({});
  const [state, setState] = useState({
    status: "none",
    results: [],
    filters: [],
    exploreOptions: [],
    resultsFields: {},
    searchValue: "",
    currentPage: 1,
    totalResults: null,
  });

  const [enterClicked, setEnterClicked] = useState({ text: "", state: false });
  const [checkedValue, setCheckedValue] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchComplete, setSearchComplete] = useState(false);
  const [showChatBox, setShowChatBox] = useState(false);
  const [selectedSort, setSelectedSort] = useState("");
  const [initialLoad, setInitialLoad] = useState(true);
  const [queryUUID, setQueryUUID] = useState(null);
  const [groupBy, setGroupBy] = useState(false);
  const [groupByPayload, setGroupByPayload] = useState([]);
  const [totalSearchPages, setTotalSearchPages] = useState(0);

  // Click outside autocomplete close
  useEffect(() => {
    const closeDropdown = (e) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(e.target)) {
        setShowOptions(false);
      }
    };
    document.addEventListener("mousedown", closeDropdown);
    return () => document.removeEventListener("mousedown", closeDropdown);
  }, []);

  const handleAutocomplete = useDebouncedCallback((e) => {
    setQueryphrase(e.target.value);
    setEnterClicked({ state: false, text: null });

    if (!e.target.value) {
      setAutoCompleteList([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    axiosInstance
      .post("/search/autocomplete", {
        search_phrase: e.target.value,
        scraped_from: "hnhiring",
      })
      .then((res) => {
        setAutoCompleteList(res.data.data.list.map((i) => ({ text: i.company_name })));
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  }, 500);

  const handleSearch = useDebouncedCallback(
    (
      searchParam,
      page = 1,
      shouldPassQuery = true,
      unusedParam = null,
      filterOverride = null,
      groupByOverride = null,
    ) => {
      if (!searchParam) {
        setSearchLoading(false);
        return;
      }

      setState((prev) => ({
        ...prev,
        status: "loading",
        searchValue: searchParam,
      }));

      setSearchLoading(true);
      setOpenFilter(false);

      const currentFilters = filterOverride !== null ? filterOverride : filterOptions;
      const currentGroupBy = groupByOverride !== null ? groupByOverride : groupByPayload;

      // Merge scraped_from into include_filters
      const filtersWithScrapedFrom = {
        include_filters: {
          ...(currentFilters.include_filters || {}),
          scraped_from: ["hnhiring"],
        },
        exclude_filters: currentFilters.exclude_filters || {},
      };

      const payload = {
        search_phrase: searchParam,
        filters: filtersWithScrapedFrom,
        sort_by: selectedSort,
        group_by: currentGroupBy,
      };

      if (shouldPassQuery && queryUUID) {
        payload.query_uuid = queryUUID;
        payload.page_no = page;
      }

      axiosInstance
        .post("/search", payload)
        .then((res) => {
          const response = res.data.data;

          setQueryUUID(response.query_uuid);
          setGroupBy(response.group_by);
          setTotalSearchPages(response.pagination?.total_pages);

          const mergedFilters = {};
          response.facets?.forEach((facet) => {
            const key = Object.keys(facet)[0];
            mergedFilters[key] = facet[key];
          });

          setMetadata({ filters: mergedFilters });

          setState((prev) => ({
            ...prev,
            results: response.results,
            resultsFields: response.results_fields,
            status: "complete",
            filters: mergedFilters,
            currentPage: page,
            totalResults: response.total,
          }));

          setSearchLoading(false);
          setSearchComplete(true);
        })
        .catch(() => {
          setSearchLoading(false);
        });
    },
    150,
  );

  const executeSearch = (value) => {
    const searchValue = value || queryphrase || state?.searchValue;
    if (!searchValue) return;

    setInitialLoad(false);
    setFilterOptions({});
    setQueryUUID(null);
    setGroupBy(false);
    setGroupByPayload([]);

    setState((prev) => ({
      ...prev,
      filters: [],
      currentPage: 1,
    }));

    setQueryphrase(searchValue);
    handleSearch(searchValue, 1);
  };

  if (metadataIsLoading) return <FullPageSpinner isVisible={true} />;

  return (
    <div className="container">
      {initialLoad && <Header />}
      {initialLoad && <Hero heroText={metadata.heroText} />}

      <Search
        queryphrase={queryphrase}
        setQueryphrase={setQueryphrase}
        handleAutocomplete={handleAutocomplete}
        executeSearch={executeSearch}
        setShowChatBox={setShowChatBox}
        showChatBox={showChatBox}
        autoCompleteList={autoCompleteList}
        searchComplete={searchComplete}
        initialLoad={initialLoad}
        domainText={metadata.domainText}
        resumeRoute="/hnhiring-resume"
      />

      {state.status !== "none" && (
        <div className="second-search-bar">
          <div className="results-section">
            <div className="results-content">
              <ListFilter
                filters={state.filters}
                filterOptions={filterOptions}
                setFilterOptions={setFilterOptions}
                handleSearch={handleSearch}
                counts={counts}
                setCheckedValue={setCheckedValue}
                setOpenFilter={setOpenFilter}
                openFilter={openFilter}
                metadata={metadata}
                searchValue={state.searchValue}
                currentPage={state.currentPage}
                setCurrentPage={(page) => setState((prev) => ({ ...prev, currentPage: page }))}
                groupBy={groupByPayload}
                setGroupBy={setGroupByPayload}
              />

              <div className="results-header">
                <div className="results-info">
                  {state.status === "loading" && (
                    <p className="results-text">
                      Searching for "{state.searchValue?.replaceAll("+", " ")}"
                    </p>
                  )}

                  {state.status === "complete" && state.results?.length > 0 && (
                    <div>
                      <p className="results-text">
                        {state.totalResults} current {state.searchValue?.replaceAll("+", " ")}{" "}
                        position active
                      </p>
                      <p className="ai-search-text">AI has found the perfect job match for you</p>
                    </div>
                  )}
                </div>

                {metadata.sortby?.length > 0 && (
                  <div className="sortby-dropdown">
                    <ResultSortBy
                      sortby={metadata.sortby}
                      selectedSort={selectedSort}
                      onChange={(value) => {
                        setSelectedSort(value);
                        handleSearch(state.searchValue, state.currentPage, true);
                      }}
                    />
                  </div>
                )}
              </div>

              {searchLoading && (
                <div className="loading-center">
                  <div className="loading-spinner" />
                </div>
              )}

              <DataListComponent
                dataListView={state.results}
                searchedKey={state.searchValue}
                searchValue={queryphrase}
                exploreOptions={state.exploreOptions}
                currentPage={state.currentPage}
                setCurrentPage={(newPage) => {
                  setState((prev) => ({ ...prev, currentPage: newPage }));
                  handleSearch(state.searchValue, newPage);
                }}
                counts={counts || {}}
                enterClicked={enterClicked}
                state={state}
                resultsFields={state.resultsFields}
                resultsFields2={metadata.resultsFields2}
                searchLoading={searchLoading}
                totalSearchPages={totalSearchPages}
                groupBy={groupBy}
                metadata={metadata}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HnHiring;